// @ts-nocheck
import { useMemo, useContext } from 'react';
import { StylesConfig, OptionTypeBase } from 'react-select';
import { ThemeContext } from 'styled-components/macro';

export const useCustomStyles = <T extends OptionTypeBase>(
  notSeparated?: boolean,
  isError?: boolean,
  isAsyncSelect?: boolean
): StylesConfig<T, false> => {
  const theme = useContext(ThemeContext);

  return useMemo<StylesConfig<T, false>>(
    () => ({
      container: (provided) => ({
        ...provided,
      }),
      control: (provided, { menuIsOpen, isDisabled }) => ({
        ...provided,
        backgroundColor: theme.colors.select.background,
        color: theme.colors.select.color,
        borderRadius: notSeparated
          ? menuIsOpen
            ? '0.5rem 0.5rem 0 0'
            : '0.5rem'
          : '0.5rem',
        padding: notSeparated
          ? '0.4375rem 0.875rem 0.4375rem 0.875rem'
          : '0.5rem 0.875rem',
        // eslint-disable-next-line no-nested-ternary
        borderColor: isError
          ? theme.colors.field.primary.border.error
          : notSeparated
          ? theme.colors.background.medium
          : theme.colors.select.default.border,
        borderWidth: '1px',
        boxShadow: 'none',
        minHeight: theme.dimensions.input.height.small,
        cursor: 'pointer',
        fontSize: '0.875rem',
        fontWeight: 500,
        letterSpacing: '-0.005em',
        transition:
          'border-color 180ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1)',
        svg: {
          height: '1em',
          width: '1em',
          fill: 'currentColor',
          fontSize: notSeparated ? '0.95rem' : '0.75rem',
        },
        opacity: isDisabled ? 0.5 : 1,
        '&:hover': {
          borderColor: theme.colors.accent,
        },
        ...(menuIsOpen && {
          borderColor: theme.colors.accent,
          boxShadow: `0 0 0 3px ${theme.colors.accent}2E`,
        }),
      }),
      indicatorSeparator: () => ({
        display: 'none',
      }),
      menu: (provided) => ({
        ...provided,
        zIndex: theme.zindexes.selectMenu,
        margin: notSeparated ? '0' : '0.375rem 0',
        boxShadow: theme.shadows.dropdown,
        borderRadius: notSeparated ? '0 0 0.5rem 0.5rem' : '0.5rem',
        borderColor: notSeparated
          ? theme.colors.select.notSeparated.menuBorder
          : theme.colors.select.default.menuBorder,
        overflow: 'hidden',
      }),
      menuList: (provided) => ({
        ...provided,
        zIndex: theme.zindexes.selectMenu,
        padding: 0,
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginRight: '1rem',
        '> div': {
          width: '100%',
          height: '100%',
        },
        '> div > span': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      }),
      option: (provided, state) => ({
        ...provided,
        cursor: 'pointer',
        zIndex: theme.zindexes.selectMenu,
        transition: '250ms ease-in-out',
        padding: notSeparated
          ? '0.5rem 1.25rem 0.5rem 1.125rem'
          : '0.5rem 0.875rem',
        backgroundColor: state.isSelected
          ? theme.colors.select.selected
          : 'transparent',
      }),
      placeholder: (provided) => ({
        ...provided,
        color: 'inherit',
        margin: 0,
        height: 'fit-content !important',
        ...(isAsyncSelect ? { lineHeight: 1.5 } : {}),
      }),
      singleValue: (provided) => ({
        ...provided,
        height: 'fit-content !important',
        ...(isAsyncSelect ? { lineHeight: 1.5 } : {}),
      }),
    }),
    [
      isError,
      notSeparated,
      theme.colors.field.primary.border.error,
      theme.colors.select.background,
      theme.colors.select.color,
      theme.colors.select.default.border,
      theme.colors.select.default.menuBorder,
      theme.colors.select.notSeparated.border,
      theme.colors.select.notSeparated.menuBorder,
      theme.colors.select.selected,
      theme.dimensions.input.height.small,
      theme.shadows.mid,
      theme.shadows.small,
    ]
  );
};
